import classNames from 'classnames';
import React, { useState } from 'react';
import type { Placement } from '@floating-ui/react-dom';
import type { ButtonProps } from '../components/Button';
import Button from '../components/Button';
import Caret from '../components/Caret';
import { MenuItemRadio, MenuSimpleItem } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import formatBoundedCount from '../utils/formatBoundedCount';
import * as styles from './PostFilterMenu.module.scss';

interface Option {
  icon: string;
  count?: number;
  label: string;
}

interface OptionWithValue<Value extends string> extends Option {
  value: Value;
}

type OptionsRecord<Type extends string> = Record<Type, Option>;

function optionsRecordToArray<Value extends string>(
  filterOptionsRecord: OptionsRecord<Value>,
): OptionWithValue<Value>[] {
  const entries = Object.entries(filterOptionsRecord) as Array<[Value, Option]>;
  return entries.map(([value, props]) => ({ value, ...props }));
}

interface PostFilterMenuProps<Value extends string> {
  menuPlacement?: Placement;
  title?: string;
  width?: number;
  options: OptionsRecord<Value>;
  value: Value;
  onChange: (value: Value) => void;
  buttonProps?: ButtonProps;
  showCountInLabel?: boolean;
}

const PostFilterMenu = <Value extends string>({
  menuPlacement = 'bottom-start',
  title,
  options: optionsRecord,
  value,
  width,
  onChange,
  buttonProps,
  showCountInLabel,
}: PostFilterMenuProps<Value>) => {
  const [isOpen, setIsOpen] = useState(false);
  const options = optionsRecordToArray<Value>(optionsRecord);
  const activeOption = options.find((option) => option.value === value);

  return (
    <div className={styles.container} style={{ width }}>
      <OverflowMenu
        placement={menuPlacement}
        open={isOpen}
        onOpenChange={setIsOpen}
        button={
          <Button
            className={classNames(styles.button, { [styles.open]: isOpen })}
            {...buttonProps}
          >
            <SVGIcon size={14}>{activeOption?.icon}</SVGIcon>
            <div className={styles.label}>
              {showCountInLabel && typeof activeOption?.count === 'number'
                ? `${activeOption.label} · ${activeOption.count}`
                : activeOption?.label}
            </div>
            <Caret direction={isOpen ? 'up' : 'down'} />
          </Button>
        }
      >
        {title && (
          <MenuSimpleItem>
            <h5 className={styles.title}>{title}</h5>
          </MenuSimpleItem>
        )}
        {options.map((option) => (
          <MenuItemRadio
            key={option.value}
            checked={activeOption?.value === option.value}
            icon={option.icon}
            onClick={() => onChange(option.value)}
            right={
              typeof option.count === 'number' ? (
                <div
                  className={classNames(styles.count, {
                    [styles.active]: activeOption?.value === option.value,
                  })}
                >
                  {formatBoundedCount(option.count, 9999)}
                </div>
              ) : null
            }
            variant="highlight"
          >
            {option.label}
          </MenuItemRadio>
        ))}
      </OverflowMenu>
    </div>
  );
};

export const POST_FILTER_MENU_PROPS = {
  title: 'Show',
  width: 132,
  menuPlacement: 'bottom-start',
} satisfies Partial<PostFilterMenuProps<string>>;

export const POST_SORT_MENU_PROPS = {
  title: 'Sort',
  width: 136,
  menuPlacement: 'bottom-end',
} satisfies Partial<PostFilterMenuProps<string>>;

export default PostFilterMenu;
